<template>
  <div
    :class="[
      'bg-white dark:bg-gray-900 overflow-hidden',
      shadow ? shadowClasses : '',
      border ? 'border border-gray-200 dark:border-gray-700' : '',
      rounded ? 'rounded-lg' : '',
      hover ? 'transition-shadow hover:shadow-lg' : ''
    ]"
  >
    <!-- Header -->
    <div
      v-if="$slots.header || title || $slots.actions"
      :class="[
        'px-6 py-4',
        headerBorder ? 'border-b border-gray-200 dark:border-gray-700' : '',
        headerBg ? 'bg-gray-50 dark:bg-gray-800' : ''
      ]"
    >
      <div class="flex items-center justify-between">
        <div class="flex-1">
          <slot name="header">
            <h3
              v-if="title"
              :class="[
                'font-semibold',
                titleSize === 'sm' ? 'text-base' : '',
                titleSize === 'md' ? 'text-lg' : '',
                titleSize === 'lg' ? 'text-xl' : '',
                'text-gray-900 dark:text-gray-100'
              ]"
            >
              {{ title }}
            </h3>
          </slot>
          <p v-if="subtitle" class="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {{ subtitle }}
          </p>
        </div>
        <div v-if="$slots.actions" class="ml-4">
          <slot name="actions" />
        </div>
      </div>
    </div>

    <!-- Content -->
    <div :class="padding ? paddingClasses : ''">
      <slot />
    </div>

    <!-- Footer -->
    <div
      v-if="$slots.footer"
      :class="[
        'px-6 py-4',
        footerBorder ? 'border-t border-gray-200 dark:border-gray-700' : '',
        footerBg ? 'bg-gray-50 dark:bg-gray-800' : ''
      ]"
    >
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  title: {
    type: String,
    default: null,
  },
  subtitle: {
    type: String,
    default: null,
  },
  titleSize: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value),
  },
  padding: {
    type: [Boolean, String],
    default: true,
  },
  shadow: {
    type: [Boolean, String],
    default: true,
  },
  border: {
    type: Boolean,
    default: true,
  },
  rounded: {
    type: Boolean,
    default: true,
  },
  hover: {
    type: Boolean,
    default: false,
  },
  headerBorder: {
    type: Boolean,
    default: false,
  },
  headerBg: {
    type: Boolean,
    default: false,
  },
  footerBorder: {
    type: Boolean,
    default: true,
  },
  footerBg: {
    type: Boolean,
    default: false,
  },
});

/**
 * Get padding classes
 */
const paddingClasses = computed(() => {
  if (typeof props.padding === 'string') {
    return props.padding;
  }
  if (props.padding === true) {
    return 'p-6';
  }
  return '';
});

/**
 * Get shadow classes
 */
const shadowClasses = computed(() => {
  if (typeof props.shadow === 'string') {
    return props.shadow;
  }
  if (props.shadow === true) {
    return 'shadow-sm';
  }
  return '';
});
</script>
